import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { SysAircraftMake } from 'app/core/models/sys-aircraft-make.model';
import { SysAircraftModel } from 'app/core/models/sys-aircraft-model.model';
import { SysTablesService } from 'app/core/services/sys-tables.service';
import {
  sortByInnerStringProperty,
  sortByNumberProperty,
  sortByStringProperty,
} from 'app/core/utils/sorting.utils';
import { Contract } from 'app/features/contracts/models/contract.model';
import { ContractHttpService } from 'app/features/contracts/services/contracts.service';
import { ManagementHttpService } from 'app/features/manage/services/management.service';
import { Aircraft } from 'app/features/settings/models/aircraft.model';
import { ContractGroupType } from 'app/features/settings/models/contract-group-type.model';
import { ContractProgram } from 'app/features/settings/models/contract-program.model';
import { SettingsHttpService } from 'app/features/settings/services/settings.service';
import { forkJoin, from, Observable, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
  withLatestFrom,
} from 'rxjs/operators';
import { CONTRACT_ACTIONS, CONTRACT_SELECTORS } from '.';
import { AIRCRAFT_ACTIONS, AIRCRAFT_SELECTORS } from '../aircraft';
import { SysClientReportType } from 'app/core/models/sys-client-report-types.model';
import { RateEscalationType } from 'app/features/settings/models/rate-escalation-type.model';
import { Product } from 'app/features/settings/models/product.model';
import { SysFlightCardComponentTypeEnum } from 'app/core/models/sys-flight-card-component-type.enum';
import { SysFlightCardComponentType } from 'app/core/models/sys-flight-card-component-type.model';
import { SysEntityTypeEnum } from 'app/core/models/sys-entity-type.enum';

@Injectable()
export class ContractEffects {
  triggerAllContractGridAPIsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerAllContractGridAPIs),
      mergeMap(() =>
        forkJoin([
          this.settingsService.getContractGroupTypes(),
          this.settingsService.getAircrafts(),
          this.sysTablesService.getSysAircraftModels(),
          this.sysTablesService.getSysAircraftMakes(),
          this.settingsService.getContractPrograms(),
          this.settingsService.getContractCaptureTypes(),
          this.sysTablesService.getSysClientReportTypes(),
          this.settingsService.getRateEscalationTypes(),
          this.sysTablesService.getSysFlightCardComponents(),
          this.sysTablesService.getSysFlightCardComponentTypes(),
          this.settingsService.getProducts(),
          this.settingsService.getContractGroupingsForAnalytics(),
          this.managementHttpService.getAdminStaffs(SysEntityTypeEnum.RegionalSalesManagers),
        ]).pipe(
          mergeMap(
            ([
              contractGroupTypes,
              aircrafts,
              aircraftModels,
              aircraftMakes,
              contractPrograms,
              contractCaptureTypes,
              sysClientReportTypes,
              rateEscalation,
              sysFlightCardComponents,
              sysFlightCardComponentTypes,
              products,
              contractGroupingAnalytics,
              regionalSaleManagerStaffs,
            ]) => {
              aircrafts = (aircrafts ?? []).map(aircraft =>
                this._getUpdatedAircraftName(aircraft, aircraftModels, aircraftMakes)
              );
              aircrafts = sortByStringProperty<Aircraft>(aircrafts, 'serialNumber');

              contractGroupTypes = (contractGroupTypes ?? []).filter(type => type.isActive);
              contractGroupTypes = sortByStringProperty<ContractGroupType>(
                contractGroupTypes,
                'nameEn'
              );

              contractGroupingAnalytics = contractGroupingAnalytics ?? [];

              aircraftModels = (aircraftModels ?? []).filter(model => model.isActive);
              aircraftModels = sortByStringProperty<SysAircraftModel>(aircraftModels, 'nameEn');

              aircraftMakes = (aircraftMakes ?? []).filter(make => make.isActive);
              aircraftMakes = sortByStringProperty<SysAircraftMake>(aircraftMakes, 'nameEn');

              contractPrograms = (contractPrograms ?? []).filter(program => program.isActive);
              contractPrograms = sortByStringProperty<ContractProgram>(contractPrograms, 'nameEn');

              rateEscalation = sortByStringProperty<RateEscalationType>(
                rateEscalation ?? [],
                'nameEn'
              );

              sysFlightCardComponents = sysFlightCardComponents ?? [];

              const index = sysFlightCardComponentTypes.findIndex(
                fcct => fcct.sysFlightCardComponentTypeId === SysFlightCardComponentTypeEnum.Engines
              );
              const engineType = sysFlightCardComponentTypes.splice(index, 1);
              sysFlightCardComponentTypes = sysFlightCardComponentTypes
                ? sortByNumberProperty<SysFlightCardComponentType>(
                    sysFlightCardComponentTypes,
                    'sysFlightCardComponentTypeId'
                  ).concat(engineType)
                : [];

              sysClientReportTypes = sortByStringProperty<SysClientReportType>(
                sysClientReportTypes ?? [],
                'nameEn'
              );
              products = sortByNumberProperty<Product>(products, 'sortOrder');
              regionalSaleManagerStaffs = sortByInnerStringProperty(regionalSaleManagerStaffs, [
                'adminStaffNavigation',
                'fullName',
              ]);
              // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
              return from([
                CONTRACT_ACTIONS.contractGroupTypesSuccess({ contractGroupTypes }),
                AIRCRAFT_ACTIONS.aircraftsSuccess({ aircrafts }),
                AIRCRAFT_ACTIONS.aircraftModelsSuccess({ aircraftModels }),
                AIRCRAFT_ACTIONS.aircraftMakesSuccess({ aircraftMakes }),
                CONTRACT_ACTIONS.contractProgramsSuccess({ contractPrograms }),
                CONTRACT_ACTIONS.setSysClientReportTypes({ sysClientReportTypes }),
                CONTRACT_ACTIONS.setContractCaptureTypesSuccess({ contractCaptureTypes }),
                CONTRACT_ACTIONS.setRateEscalationSuccess({ rateEscalation }),
                CONTRACT_ACTIONS.setSysFlightCardComponents({ sysFlightCardComponents }),
                CONTRACT_ACTIONS.setSysFlightCardComponentTypes({ sysFlightCardComponentTypes }),
                CONTRACT_ACTIONS.setProductSuccess({ products }),
                CONTRACT_ACTIONS.contractGroupingForAnalyticSuccess({
                  contractGroupingAnalytics,
                }),
                CONTRACT_ACTIONS.adminStaffsForRegionalSalesManager({
                  regionalSaleManagerStaffs,
                }),
              ]);
            }
          )
        )
      )
    );
  });

  loadContractGroupTypesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerContractGroupTypes),
      exhaustMap(() =>
        this.settingsService.getContractGroupTypes().pipe(
          map((contractGroupTypes: ContractGroupType[]) =>
            CONTRACT_ACTIONS.contractGroupTypesSuccess({
              contractGroupTypes: sortByStringProperty<ContractGroupType>(
                contractGroupTypes,
                'nameEn'
              ),
            })
          ),
          catchError(error =>
            of(CONTRACT_ACTIONS.contractGroupTypesFailure({ error: error.message }))
          )
        )
      )
    );
  });

  loadContractProgramsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerContractPrograms),
      exhaustMap(() =>
        this.settingsService.getContractPrograms().pipe(
          map((contractPrograms: ContractProgram[]) =>
            CONTRACT_ACTIONS.contractProgramsSuccess({
              contractPrograms: sortByStringProperty<ContractProgram>(contractPrograms, 'nameEn'),
            })
          ),
          catchError(error =>
            of(CONTRACT_ACTIONS.contractProgramsFailure({ error: error.message }))
          )
        )
      )
    );
  });

  loadSysFlightCardComponentsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerSysFlightCardComponents),
      exhaustMap(() =>
        this.sysTablesService.getSysFlightCardComponents().pipe(
          map(sysFlightCardComponents =>
            CONTRACT_ACTIONS.setSysFlightCardComponents({
              sysFlightCardComponents,
            })
          ),
          catchError(error =>
            of(CONTRACT_ACTIONS.sysFlightCardComponentsFailure({ error: error.message }))
          )
        )
      )
    );
  });

  addContractEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerAddContract),
      // eslint-disable-next-line @ngrx/prefer-concat-latest-from
      withLatestFrom(
        this.store.select(CONTRACT_SELECTORS.selectAllContractsState),
        this.store.select(CONTRACT_SELECTORS.selectAllContractGroupTypesState),
        this.store.select(CONTRACT_SELECTORS.selectAllContractProgramsState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftModelsState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftMakesState)
      ),
      switchMap(
        ([
          action,
          contracts,
          contractGroupTypes,
          contractPrograms,
          aircrafts,
          aircraftModels,
          aircraftMakes,
        ]) =>
          this.contractService.insertContract(action?.formGroup).pipe(
            mergeMap((id: number) => {
              contractGroupTypes = (contractGroupTypes ?? []).filter(type => type.isActive);
              aircraftModels = (aircraftModels ?? []).filter(model => model.isActive);
              aircraftMakes = (aircraftMakes ?? []).filter(make => make.isActive);
              aircrafts = (aircrafts ?? []).map(aircraft =>
                this._getUpdatedAircraftName(aircraft, aircraftModels, aircraftMakes)
              );
              contractPrograms = (contractPrograms ?? []).filter(program => program.isActive);

              const addedContract: Contract = {
                ...action?.formGroup.value,
                contractId: id,
                ...this._updateReferenceFields(
                  action?.formGroup?.value,
                  contractGroupTypes,
                  aircrafts
                ),
              };
              // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
              return [
                CONTRACT_ACTIONS.updateGridAfterAddContractSuccess({
                  addedContractId: addedContract.contractId,
                }),
                CONTRACT_ACTIONS.contractsSuccess({ contracts }),
              ];
            }),
            catchError(error => of(CONTRACT_ACTIONS.AddContractFailure({ error: error.message })))
          )
      )
    );
  });

  editContractEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.triggerEditContract),
      // eslint-disable-next-line @ngrx/prefer-concat-latest-from
      withLatestFrom(
        this.store.select(CONTRACT_SELECTORS.selectAllContractsState),
        this.store.select(CONTRACT_SELECTORS.selectAllContractGroupTypesState),
        this.store.select(CONTRACT_SELECTORS.selectAllContractProgramsState),
        this.store.select(CONTRACT_SELECTORS.selectCurrentContractState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftModelsState),
        this.store.select(AIRCRAFT_SELECTORS.selectAllAircraftMakesState)
      ),
      switchMap(
        ([
          action,
          contracts,
          contractGroupTypes,
          contractPrograms,
          currContract,
          aircrafts,
          aircraftModels,
          aircraftMakes,
        ]) =>
          this.contractService.patchContract(action?.formGroup).pipe(
            mergeMap(() => {
              contractGroupTypes = (contractGroupTypes ?? []).filter(type => type.isActive);
              aircraftModels = (aircraftModels ?? []).filter(model => model.isActive);
              aircraftMakes = (aircraftMakes ?? []).filter(make => make.isActive);
              aircrafts = (aircrafts ?? []).map(aircraft =>
                this._getUpdatedAircraftName(aircraft, aircraftModels, aircraftMakes)
              );
              contractPrograms = (contractPrograms ?? []).filter(program => program.isActive);

              currContract = {
                ...currContract,
                ...action?.formGroup.value,
                ...this._updateReferenceFields(
                  action?.formGroup?.value,
                  contractGroupTypes,
                  aircrafts
                ),
              };

              let allContracts = contracts.map(cont =>
                cont.contractId === currContract.contractId ? currContract : cont
              );

              allContracts = sortByStringProperty<Contract>(allContracts, 'contractNumber');
              // eslint-disable-next-line @ngrx/no-multiple-actions-in-effects
              return [
                CONTRACT_ACTIONS.contractsSuccess({ contracts: allContracts }),
                CONTRACT_ACTIONS.setCurrentContract({ currentContract: currContract }),
                AIRCRAFT_ACTIONS.aircraftsSuccess({ aircrafts }),
              ];
            }),
            catchError(error => of(CONTRACT_ACTIONS.AddContractFailure({ error: error.message })))
          )
      )
    );
  });

  contractFlightCardComponentsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract),
      mergeMap(action => {
        return this._fetchContractFlightCardComponents(action.currentContract.contractId);
      })
    );
  });

  contractFlightCardComponentsUpdateEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.updateContractFlightCardComponents),
      mergeMap(action => {
        return this._fetchContractFlightCardComponents(action.contractId);
      })
    );
  });

  revenueContractFlightCardComponentsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setFlightCardComponentsForRevenue),
      mergeMap(action => {
        // (please dont remove these comments) temporary measure to update this in contract state slice
        // can be moved to a new "revenue" state if we have more use cases in future
        return this._fetchContractFlightCardComponents(action.contractId);
      })
    );
  });

  adminStaffsEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.getAdminStaffs),
      mergeMap(() => {
        return this.managementHttpService.getAdminStaffs(SysEntityTypeEnum.AccountManager).pipe(
          map(adminStaffs => {
            adminStaffs = sortByInnerStringProperty(adminStaffs, [
              'adminStaffNavigation',
              'fullName',
            ]);
            return CONTRACT_ACTIONS.setAdminStaffs({
              adminStaffs: adminStaffs,
            });
          })
        );
      })
    );
  });

  contractStatusEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.getSysContractStatusesAndChangeReasons),
      mergeMap(() => {
        return forkJoin([
          this.sysTablesService.getSysContractStatuses(),
          this.sysTablesService.getSysContractStatusChangeReasons(),
        ]).pipe(
          map(([sysContractStatus, sysContractStatusChangeReason]) => {
            return of(
              CONTRACT_ACTIONS.setSysContractStatuses({
                sysContractStatuses: sortByNumberProperty(sysContractStatus ?? [], 'sortOrder'),
              }),
              CONTRACT_ACTIONS.setSysContractStatusChangeReasons({
                sysContractStatusChangeReasons: sortByNumberProperty(
                  sysContractStatusChangeReason ?? [],
                  'sortOrder'
                ),
              })
            );
          }),
          concatMap(action => action)
        );
      })
    );
  });

  contractBillingEntitiesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract, CONTRACT_ACTIONS.setCurrentContractForInvoice),
      mergeMap(action => {
        return this.contractService
          .getContractBillingEntitiesForContract(action.currentContract.contractId)
          .pipe(
            map(billingEntities => {
              return CONTRACT_ACTIONS.setContractBillingEntitySuccess({
                contractBillingEntities: billingEntities,
              });
            })
          );
      })
    );
  });

  contractEntitiesEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract),
      mergeMap(action => {
        return this.contractService.getContractEntities(action.currentContract.contractId).pipe(
          map(entities => {
            const contractEntities = sortByInnerStringProperty(entities ?? [], [
              'entity',
              'fullName',
            ]);
            return CONTRACT_ACTIONS.setContractEntitySuccess({
              contractEntities: contractEntities,
            });
          })
        );
      })
    );
  });

  loadContractTransfers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract),
      mergeMap(action =>
        this.contractService
          .getContractTransfers(action.currentContract.contractId)
          .pipe(
            map(contractTransfers =>
              CONTRACT_ACTIONS.loadContractTransfersSuccess({ contractTransfers })
            )
          )
      )
    );
  });

  loadContractProductShortNames$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        CONTRACT_ACTIONS.setCurrentContract,
        CONTRACT_ACTIONS.triggerContractProductShortNames
      ),
      mergeMap(action =>
        this.contractService.getProductSummaryForContract(action.currentContract.contractId).pipe(
          map(res =>
            CONTRACT_ACTIONS.setContractProductShortNames({
              contractProductShortNames: res?.contractProductShortNames?.replace(/,/g, ', '),
            })
          )
        )
      )
    );
  });

  loadContractProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract, CONTRACT_ACTIONS.triggerContractProducts),
      mergeMap(action =>
        this.contractService.getContractProducts(action.currentContract.contractId).pipe(
          map(contractProducts => {
            return CONTRACT_ACTIONS.setContractProductSuccess({ contractProducts });
          })
        )
      )
    );
  });

  loadProducts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContractForInvoice),
      mergeMap(() =>
        this.settingsService.getProducts().pipe(
          map(products => {
            products = sortByStringProperty<Product>(products, 'nameEn');
            return CONTRACT_ACTIONS.setProductSuccess({ products });
          })
        )
      )
    );
  });

  loadNextFlightCard$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(CONTRACT_ACTIONS.setCurrentContract),
      mergeMap(action =>
        this.contractService.getNextFlightCard(action.currentContract.contractId).pipe(
          map(nextFlightCards => {
            return CONTRACT_ACTIONS.setContractNextFlightCard({ nextFlightCards });
          })
        )
      )
    );
  });

  private _fetchContractFlightCardComponents(contractId: number): Observable<Action> {
    return forkJoin([
      this.contractService.getContractFlightCardComponents(contractId),
      this.contractService.getFlightCardComponentTypesBasedOnContract(contractId),
    ]).pipe(
      map(([contractFlightCardComponents, types]) => {
        return CONTRACT_ACTIONS.setContractFlightCardComponents({
          contractFlightCardComponents: contractFlightCardComponents.map(cfcc => {
            return {
              ...cfcc,
              hasProduct:
                types.find(
                  type => type.sysFlightCardComponentTypeId === cfcc.sysFlightCardComponentTypeId
                )?.hasProduct ?? false,
              isRecurring:
                types.find(
                  type => type.sysFlightCardComponentTypeId === cfcc.sysFlightCardComponentTypeId
                )?.isReccuring ?? false,
            };
          }),
        });
      })
    );
  }

  private _getUpdatedAircraftName(
    aircraft: Aircraft,
    aircraftModels: SysAircraftModel[],
    aircraftMakes: SysAircraftMake[]
  ): Aircraft {
    const aircraftModel = aircraftModels.find(model => model.id === aircraft?.sysAircraftModelId);
    const aircraftMake = aircraftMakes.find(make => make.id === aircraftModel?.sysAircraftMakeId);
    return {
      ...aircraft,
      sysAircraftModelName: `${aircraft.serialNumber} - ${aircraftMake?.nameEn} ${aircraftModel?.nameEn}`,
    };
  }

  private _updateReferenceFields(
    ref: Contract,
    contractGroupTypes: ContractGroupType[],
    aircrafts: Aircraft[]
  ) {
    const contractGroupType = contractGroupTypes.find(type => type.id === ref.contractGroupTypeId);
    const aircraft = aircrafts.find(aircraftUnit => aircraftUnit.id === ref.aircraftId);

    return {
      // TO DO Not needed for initial get, but useful  for edits -> needed?
      contractGroupTypeName: contractGroupType?.nameEn || '',
      aircraftName: aircraft?.sysAircraftModelName || '',
    };
  }

  constructor(
    private actions$: Actions,
    private contractService: ContractHttpService,
    private settingsService: SettingsHttpService,
    private sysTablesService: SysTablesService,
    private managementHttpService: ManagementHttpService,
    private store: Store
  ) {}
}
